import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import BaseMap, { MapPosition } from '../map/BaseMap';
import GoogleMapProvider, { useGoogleMapContext } from '../map/GoogleMapProvider';
import { Loader2, MapPin, Users, Home, CheckCircle2, Clock, Phone, Mail, Car, Star, Calendar, AlertTriangle } from 'lucide-react';
import {
  ProjectLifecycleReport as ReportData,
  LifecycleLead,
  DETERMINATION_COLORS,
  STATUS_COLORS,
} from '../../services/reporting';
import { LeadDetermination, LeadStatus } from '../../types/lead.types';

interface Props {
  report: ReportData | null;
  loading: boolean;
}

const detLabel = (d: LeadDetermination) => d.charAt(0).toUpperCase() + d.slice(1);
const statusLabel = (s: LeadStatus | string) =>
  s === 'dnc' ? 'Do Not Contact' : String(s).charAt(0).toUpperCase() + String(s).slice(1);

// ---- shared pills -----------------------------------------------------------

const DetPill: React.FC<{ det: LeadDetermination }> = ({ det }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
    style={{ backgroundColor: `${DETERMINATION_COLORS[det]}1A`, color: DETERMINATION_COLORS[det] }}
  >
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DETERMINATION_COLORS[det] }} />
    {detLabel(det)}
  </span>
);

const StatusPill: React.FC<{ status: LeadStatus }> = ({ status }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
    style={{ backgroundColor: `${STATUS_COLORS[status]}14`, color: STATUS_COLORS[status] }}
  >
    {statusLabel(status)}
  </span>
);

// ---- map --------------------------------------------------------------------

interface MapLead {
  id: string;
  parentName: string;
  determination: LeadDetermination;
  lat?: number;
  lng?: number;
  addressLine: string;
  // Signature projects: where the collector stood at signing — clicking the
  // parent marker draws home → signing-spot.
  sigLat?: number;
  sigLng?: number;
}

const createMarkerIcon = (color: string): string => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="32" viewBox="0 0 24 36">
      <path fill="${color}" stroke="#FFFFFF" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/>
      <circle fill="#FFFFFF" cx="12" cy="12" r="4.5"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

interface ResolvedPoint {
  id: string;
  parentName: string;
  determination: LeadDetermination;
  pos: MapPosition;
  sig?: MapPosition; // collector position at signing (signature projects)
}

const LifecycleMapInner: React.FC<{ leads: MapLead[] }> = ({ leads }) => {
  const { isLoaded } = useGoogleMapContext();
  const [points, setPoints] = useState<ResolvedPoint[]>([]);
  const [hover, setHover] = useState<ResolvedPoint | null>(null);
  // Clicked parent — draws home → signing-spot when the lead has signature GPS.
  const [selected, setSelected] = useState<ResolvedPoint | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;

    const sigOf = (l: MapLead): MapPosition | undefined =>
      typeof l.sigLat === 'number' && typeof l.sigLng === 'number' ? { lat: l.sigLat, lng: l.sigLng } : undefined;
    const withGps: ResolvedPoint[] = leads
      .filter((l) => typeof l.lat === 'number' && typeof l.lng === 'number')
      .map((l) => ({ id: l.id, parentName: l.parentName, determination: l.determination, pos: { lat: l.lat!, lng: l.lng! }, sig: sigOf(l) }));
    setPoints(withGps);

    const need = leads.filter(
      (l) => !(typeof l.lat === 'number' && typeof l.lng === 'number') && l.addressLine && l.addressLine !== '—'
    );
    if (need.length) {
      (async () => {
        if (!geocoderRef.current) geocoderRef.current = new google.maps.Geocoder();
        for (const l of need) {
          if (cancelled) return;
          const pos = await new Promise<MapPosition | null>((resolve) => {
            geocoderRef.current!.geocode({ address: l.addressLine }, (r, s) => {
              if (s === 'OK' && r && r[0]) {
                const loc = r[0].geometry.location;
                resolve({ lat: loc.lat(), lng: loc.lng() });
              } else resolve(null);
            });
          });
          if (pos && !cancelled) {
            setPoints((prev) => [...prev, { id: l.id, parentName: l.parentName, determination: l.determination, pos, sig: sigOf(l) }]);
          }
          await new Promise((r) => setTimeout(r, 120));
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [isLoaded, leads]);

  const center = useMemo((): MapPosition => {
    if (!points.length) return { lat: 39.8283, lng: -98.5795 };
    return {
      lat: points.reduce((s, p) => s + p.pos.lat, 0) / points.length,
      lng: points.reduce((s, p) => s + p.pos.lng, 0) / points.length,
    };
  }, [points]);

  const zoom = useMemo(() => {
    if (points.length <= 1) return 12;
    const lats = points.map((p) => p.pos.lat);
    const lngs = points.map((p) => p.pos.lng);
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    if (spread > 5) return 7;
    if (spread > 2) return 9;
    if (spread > 0.5) return 11;
    if (spread > 0.1) return 12;
    return 13;
  }, [points]);

  if (!points.length) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
        No parents have mappable locations.
      </div>
    );
  }

  return (
    <BaseMap center={center} zoom={zoom} height="440px" className="rounded-lg">
      {points.map((p) => (
        <Marker
          key={p.id}
          position={p.pos}
          icon={{
            url: createMarkerIcon(DETERMINATION_COLORS[p.determination]),
            scaledSize: new google.maps.Size(22, 32),
            anchor: new google.maps.Point(11, 32),
          }}
          onMouseOver={() => setHover(p)}
          onMouseOut={() => setHover(null)}
          onClick={() => setSelected((cur) => (cur?.id === p.id ? null : p))}
        />
      ))}
      {selected?.sig && (
        <>
          <Polyline
            path={[selected.pos, selected.sig]}
            options={{ strokeColor: '#4F46E5', strokeOpacity: 0.85, strokeWeight: 2.5, geodesic: true }}
          />
          <Marker
            position={selected.sig}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#059669',
              fillOpacity: 0.95,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 7,
            }}
          />
          <InfoWindow position={selected.sig} options={{ pixelOffset: new google.maps.Size(0, -10) }} onCloseClick={() => setSelected(null)}>
            <div className="p-0.5 text-xs text-gray-700">
              <p className="font-semibold text-emerald-700">Signature captured here</p>
              <p>
                {Math.round(
                  6371000 * 2 * Math.asin(Math.sqrt(
                    Math.sin(((selected.sig.lat - selected.pos.lat) * Math.PI) / 360) ** 2 +
                    Math.cos((selected.pos.lat * Math.PI) / 180) * Math.cos((selected.sig.lat * Math.PI) / 180) *
                    Math.sin(((selected.sig.lng - selected.pos.lng) * Math.PI) / 360) ** 2
                  ))
                )}{' '}m from {selected.parentName}&apos;s home
              </p>
            </div>
          </InfoWindow>
        </>
      )}
      {selected && !selected.sig && (
        <InfoWindow position={selected.pos} options={{ pixelOffset: new google.maps.Size(0, -32) }} onCloseClick={() => setSelected(null)}>
          <div className="p-0.5 text-xs text-gray-600">No signature location recorded for {selected.parentName}.</div>
        </InfoWindow>
      )}
      {hover && (
        <InfoWindow position={hover.pos} options={{ pixelOffset: new google.maps.Size(0, -32), disableAutoPan: true }} onCloseClick={() => setHover(null)}>
          <div className="p-0.5">
            <p className="font-semibold text-gray-900 text-sm">{hover.parentName}</p>
            <p className="text-xs mt-0.5" style={{ color: DETERMINATION_COLORS[hover.determination] }}>
              {detLabel(hover.determination)}
            </p>
          </div>
        </InfoWindow>
      )}
    </BaseMap>
  );
};

const LifecycleMap: React.FC<{ leads: MapLead[] }> = ({ leads }) => (
  <GoogleMapProvider>
    <LifecycleMapInner leads={leads} />
  </GoogleMapProvider>
);

// ---- small building blocks --------------------------------------------------

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string; color?: string }> = ({
  icon,
  label,
  value,
  sub,
  color = '#4F46E5',
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-2 text-gray-500">
      <span style={{ color }}>{icon}</span>
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="mt-1.5 text-2xl font-bold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

// Prominent, headline contract-progress panel.
const ContractProgress: React.FC<{ c: NonNullable<ReportData['summary']['contract']>; households?: boolean }> = ({ c, households }) => {
  const Bar: React.FC<{ label: string; current: number; goal: number; note?: string; done?: boolean }> = ({
    label,
    current,
    goal,
    note,
    done,
  }) => {
    const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
    return (
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-gray-600">{label}</span>
          <span className="text-sm">
            <span className="text-2xl font-bold text-gray-900">{current}</span>
            <span className="text-gray-400"> / {goal}</span>
            <span className="text-gray-400 ml-1">({pct}%)</span>
          </span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: done ? '#10B981' : '#4F46E5' }} />
        </div>
        {note && <div className="text-xs text-gray-400 mt-1">{note}</div>}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contract Progress</h3>
        {c.contractSatisfied ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Contract satisfied
          </span>
        ) : c.enrollmentGoal > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
            {c.enrollmentGoal - c.enrollmentsCurrent} enrollment{c.enrollmentGoal - c.enrollmentsCurrent === 1 ? '' : 's'} to satisfy
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {c.enrollmentGoal > 0 && (
        <Bar
          label={households ? 'Enrolled households' : 'Enrolled students'}
          current={c.enrollmentsCurrent}
          goal={c.enrollmentGoal}
          note="Reaching the target satisfies the contract"
          done={c.contractSatisfied}
        />
        )}
        <Bar label={households ? 'Signed households' : 'Valid students'} current={c.leadsCurrent} goal={c.leadGoal} note={households ? 'Signature target' : 'Still required in full'} done={c.leadsCurrent >= c.leadGoal} />
        {c.conversionTarget > 0 && (
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-gray-600">Conversion</span>
            <span className="text-sm">
              <span className="text-2xl font-bold" style={{ color: c.conversionMet ? '#10B981' : '#EF4444' }}>
                {c.conversionCurrent}%
              </span>
            </span>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, c.conversionTarget > 0 ? Math.round((c.conversionCurrent / c.conversionTarget) * 100) : 0)}%`,
                backgroundColor: c.conversionMet ? '#10B981' : '#EF4444',
              }}
            />
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Requirement: ≥ {c.conversionTarget}% {c.conversionMet ? '· met ✓' : '· below target'}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

// ---- per-lead section -------------------------------------------------------

const TIMELINE_DOT: Record<string, string> = {
  captured: '#4F46E5',
  determination: '#8B5CF6',
  recap: '#0EA5E9',
  appointment: '#F59E0B',
  contact: '#6B7280',
};

const LeadSection: React.FC<{ lead: LifecycleLead; index: number }> = ({ lead, index }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    style={{ borderLeft: `4px solid ${DETERMINATION_COLORS[lead.determination]}` }}
  >
    <div className="p-4">
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
          <h4 className="text-base font-semibold text-gray-900">{lead.parentName}</h4>
          <DetPill det={lead.determination} />
          <StatusPill status={lead.status} />
          {lead.isDuplicate && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
              <AlertTriangle className="h-3 w-3" /> possible duplicate
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">{lead.studentCount} student{lead.studentCount === 1 ? '' : 's'}</span>
      </div>

      {/* info grid */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div className="flex items-start gap-2 text-gray-600">
          <MapPin className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
          <span>{lead.addressLine}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <Home className="h-4 w-4 text-gray-400 shrink-0" />
          <span>Campus: <span className="font-medium text-gray-800">{lead.campus}</span></span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <Users className="h-4 w-4 text-gray-400 shrink-0" />
          <span>Captured by <span className="font-medium text-gray-800">{lead.canvasser}</span> · {lead.capturedLabel}</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600 flex-wrap">
          {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-gray-400" />{lead.phone}</span>}
          {lead.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-gray-400" />{lead.email}</span>}
          {lead.language && <span className="text-gray-400">· {lead.language}</span>}
        </div>
      </div>

      {(lead.rideProvided || lead.attendEvent) && (
        <div className="mt-2 flex items-center gap-2">
          {lead.rideProvided && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"><Car className="h-3 w-3" /> ride offered</span>
          )}
          {lead.attendEvent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-violet-50 text-violet-700"><Star className="h-3 w-3" /> event RSVP</span>
          )}
        </div>
      )}

      {/* students */}
      {lead.students.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Students</p>
          <div className="space-y-1">
            {lead.students.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: DETERMINATION_COLORS[s.determination] }} />
                <span className="font-medium">{s.name}</span>
                <span className="text-gray-400">· {s.grade === '—' ? 'grade —' : `${s.grade} grade`}</span>
                {s.campus && s.campus !== lead.campus && <span className="text-gray-400">· {s.campus}</span>}
                <span className="text-gray-300">·</span>
                <span style={{ color: DETERMINATION_COLORS[s.determination] }}>{detLabel(s.determination)}</span>
                <span className="text-gray-300">/</span>
                <span style={{ color: STATUS_COLORS[s.status] }}>{statusLabel(s.status)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* why */}
      <div
        className="mt-3 rounded-md px-3 py-2 text-sm"
        style={{ backgroundColor: `${DETERMINATION_COLORS[lead.determination]}0F`, color: '#374151' }}
      >
        <span className="font-semibold" style={{ color: DETERMINATION_COLORS[lead.determination] }}>
          Why {detLabel(lead.determination)}:
        </span>{' '}
        {lead.determinationReason}
      </div>

      {/* interest signature + submission location (signature projects) */}
      {lead.signature && (
        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 flex items-center gap-4">
          <img
            src={lead.signature}
            alt="Signature of interest"
            className="h-14 w-40 object-contain bg-white border border-gray-200 rounded"
          />
          <div className="text-xs text-gray-600 space-y-0.5">
            <p className="font-semibold text-gray-700">Signature of interest captured</p>
            {lead.signatureCapturedAt && <p>Signed {lead.signatureCapturedAt.toLocaleString()}</p>}
            <p>
              {lead.signatureLat != null && lead.signatureLng != null
                ? <>Submitted at {lead.signatureLat.toFixed(6)}, {lead.signatureLng.toFixed(6)}{lead.signatureAccuracy != null ? ` (±${Math.round(lead.signatureAccuracy)}m)` : ''}</>
                : 'Submission GPS not recorded'}
            </p>
            <p>{lead.consentToContact ? 'Consented to follow-up contact' : 'No contact consent given'}{lead.howHeard ? ` · Heard via: ${lead.howHeard}` : ''}{lead.isBusiness ? ` · Business${lead.businessName ? `: ${lead.businessName}` : ''}${lead.businessRole ? ` (signer: ${lead.businessRole})` : ''}${lead.businessAddress ? ` — ${lead.businessAddress}` : ''}` : ''}</p>
          </div>
        </div>
      )}

      {/* timeline */}
      <div className="mt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Lifecycle timeline</p>
        <ol className="relative border-l border-gray-200 ml-1.5 space-y-2">
          {lead.timeline.map((e, i) => (
            <li key={i} className="ml-4">
              <span
                className="absolute -left-[5px] w-2.5 h-2.5 rounded-full border-2 border-white"
                style={{ backgroundColor: TIMELINE_DOT[e.kind] || '#6B7280' }}
              />
              <div className="text-sm text-gray-700">
                <span className="text-gray-400 text-xs mr-2">{e.dateLabel}</span>
                <span className="font-medium">{e.label}</span>
                {e.detail && <span className="text-gray-500"> — {e.detail}</span>}
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-2 text-xs text-gray-400 flex items-center gap-3">
          {lead.contactAttempts > 0 && <span><Clock className="h-3 w-3 inline mr-1" />{lead.contactAttempts} contact attempt{lead.contactAttempts === 1 ? '' : 's'}</span>}
          {lead.lastContactedLabel && <span>Last contacted {lead.lastContactedLabel}</span>}
          {lead.appointments.length > 0 && <span><Calendar className="h-3 w-3 inline mr-1" />{lead.appointments.length} tour{lead.appointments.length === 1 ? '' : 's'}</span>}
        </div>
      </div>
    </div>
  </div>
);

// ---- main -------------------------------------------------------------------

const ProjectLifecycleReport: React.FC<Props> = ({ report, loading }) => {
  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Assembling project recap...</span>
      </div>
    );
  }
  if (!report) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No data for this project yet.</div>;
  }

  const { summary, leads } = report;
  const mapLeads: MapLead[] = leads.map((l) => ({
    id: l.id,
    parentName: l.parentName,
    determination: l.determination,
    lat: l.lat,
    lng: l.lng,
    addressLine: l.addressLine,
    sigLat: l.signatureLat,
    sigLng: l.signatureLng,
  }));

  const detOrder: LeadDetermination[] = ['valid', 'invalid', 'pending'];
  const statusOrder: LeadStatus[] = ['uncontacted', 'cold', 'warm', 'hot', 'enrolled', 'lost', 'dnc'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900">{summary.projectName} — Lifecycle Recap</h2>
        <p className="text-sm text-gray-500 mt-1">
          {summary.dateRangeLabel} · generated {summary.generatedAtLabel}
        </p>
        <p className="text-sm text-gray-500">
          Activity {summary.firstLeadLabel} → {summary.lastLeadLabel}
        </p>
      </div>

      {/* Contract progress (headline) */}
      {summary.contract && <ContractProgress c={summary.contract} households={summary.unitMode === 'households'} />}
      {summary.contract && summary.unitMode !== 'households' && (
        <p className="text-xs text-gray-400 -mt-3 px-1">
          Enrollments are counted per student, assuming every student in an enrolled family enrolled.
          Please alert us of any students who did not enroll and we'll remove them from our records.
        </p>
      )}

      {/* Stat cards — unit follows the project's counting mode */}
      {summary.unitMode === 'households' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<Users className="h-4 w-4" />} label="Households" value={summary.totalLeads} sub={`${summary.totalStudents} students listed`} />
          <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Signed / valid" value={summary.leadsByDetermination.valid} sub="households" color={DETERMINATION_COLORS.valid} />
          <StatCard icon={<AlertTriangle className="h-4 w-4" />} label="Invalid" value={summary.leadsByDetermination.invalid} sub="households" color={DETERMINATION_COLORS.invalid} />
          <StatCard icon={<Clock className="h-4 w-4" />} label="Pending" value={summary.leadsByDetermination.pending} sub="households" color={DETERMINATION_COLORS.pending} />
        </div>
      ) : (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Users className="h-4 w-4" />} label="Students" value={summary.totalStudents} sub={`${summary.totalLeads} parents`} />
        <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Valid students" value={summary.studentsByDetermination.valid} sub={`${summary.leadsByDetermination.valid} families`} color={DETERMINATION_COLORS.valid} />
        <StatCard icon={<AlertTriangle className="h-4 w-4" />} label="Invalid students" value={summary.studentsByDetermination.invalid} sub={`${summary.leadsByDetermination.invalid} families`} color={DETERMINATION_COLORS.invalid} />
        <StatCard icon={<Clock className="h-4 w-4" />} label="Pending students" value={summary.studentsByDetermination.pending} sub={`${summary.leadsByDetermination.pending} families`} color={DETERMINATION_COLORS.pending} />
      </div>
      )}

      {/* Map + breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Parent map</h3>
            <div className="flex items-center gap-3 text-xs">
              {detOrder.map((d) => (
                <span key={d} className="flex items-center gap-1 text-gray-500">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DETERMINATION_COLORS[d] }} />
                  {detLabel(d)}
                </span>
              ))}
            </div>
          </div>
          <LifecycleMap leads={mapLeads} />
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Status breakdown</h3>
            <div className="space-y-1.5">
              {statusOrder
                .filter((s) => summary.leadsByStatus[s] > 0)
                .map((s) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] }} />
                      {statusLabel(s)}
                    </span>
                    <span className="font-medium text-gray-900">{summary.leadsByStatus[s]}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Campus + canvasser tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">By campus</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase tracking-wide">
                <th className="text-left font-medium pb-1">Campus</th>
                <th className="text-right font-medium pb-1">Parents</th>
                <th className="text-right font-medium pb-1">Students</th>
                <th className="text-right font-medium pb-1">Valid</th>
                <th className="text-right font-medium pb-1">Enrolled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.byCampus.map((c) => (
                <tr key={c.name}>
                  <td className="py-1.5 text-gray-800">{c.name}</td>
                  <td className="py-1.5 text-right text-gray-600">{c.leads}</td>
                  <td className="py-1.5 text-right text-gray-600">{c.students}</td>
                  <td className="py-1.5 text-right font-medium" style={{ color: DETERMINATION_COLORS.valid }}>{c.valid}</td>
                  <td className="py-1.5 text-right text-gray-600">{c.enrolled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">By canvasser</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase tracking-wide">
                <th className="text-left font-medium pb-1">Canvasser</th>
                <th className="text-right font-medium pb-1">Parents</th>
                <th className="text-right font-medium pb-1">Students</th>
                <th className="text-right font-medium pb-1">Valid students</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.byCanvasser.map((c) => (
                <tr key={c.name}>
                  <td className="py-1.5 text-gray-800">{c.name}</td>
                  <td className="py-1.5 text-right text-gray-600">{c.leads}</td>
                  <td className="py-1.5 text-right text-gray-600">{c.students}</td>
                  <td className="py-1.5 text-right font-medium" style={{ color: DETERMINATION_COLORS.valid }}>{c.validStudents}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data flags */}
      {summary.dataFlags.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-1.5">
            <AlertTriangle className="h-4 w-4" /> Data-quality notes
          </h3>
          <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
            {summary.dataFlags.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-lead sections */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Lead-by-lead breakdown</h3>
          <span className="text-sm text-gray-400">{leads.length} parents · valid → invalid → pending</span>
        </div>
        <div className="space-y-3">
          {leads.map((lead, i) => (
            <LeadSection key={lead.id} lead={lead} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectLifecycleReport;
